import React from "react";
import { connect } from "react-redux";
import { Form, Card, Col, Row, Tab, Nav } from 'react-bootstrap';
import PropTypes from "prop-types";

import { deepEqual } from "../../utils/object";

import Personal from "./person";
import Tax from "./person/tax";
import Timecard from "./timecard";
import Shares from "./shares";
import Email from "./email";

import Calendar from "../../layout/calendar";
import ConditionalButton from "../../layout/conditional-button";
import ConditionalDropdown from "../../layout/conditional-dropdown";

import { me as myPersonRecord, mine as saveMyPersonRecord } from "../../actions/people";
import { tax as myTax, me as myStaffRecord } from "../../actions/staff";
import { send as pageInfo, loading } from "../../actions/page";
import { send as sendAlert } from "../../actions/alert";

const COMMUNICATION_PREFERENCES = {
    DIRECT_MESSAGE_LEAD_TASK_EVENT: 4,
    DIRECT_MESSAGE_BOARD_TASK_EVENT: 8,
};

const hasCommunicationPreference = (value, flag) => (Number(value || 0) & flag) === flag;

class Profile extends React.Component {
    constructor(props) {
        super(props);
        this.emailRef = React.createRef();
        this.state = {
            person: {
                flags: 0,
                uuid: null
            },
            logs: [],
            selected: "profile",
            modal: {
                event: {
                    show: false
                },
                multinput: {
                    show: false
                },
                shares: {
                    show: false
                }
            },
            Codes: {
                activities: [],
                attorneys: [],
                reports: []
            },
            saving: false
        };
        
    }

    componentDidUpdate(prevProps) {
        if (
            !deepEqual(prevProps.person, this.props.person) &&
            !deepEqual(this.props.person, this.state.person)
        ) {
            const flags = this.props.person?.flags ?? 0;
            const isStaff = (flags & 8) > 0;
            this.setState({
                person: {
                    flags: 0,
                    ...(this.props.person || {}),
                },
                selected: !isStaff && ["email", "withholding", "timecard"].includes(this.state.selected) ? "profile" : this.state.selected,
            });
        }
    }

    componentDidMount = async () => {
        setTimeout(() => {
            this.loadData();
        }, 30);
    }

    loadData = async () => {
        this.props.loading(true);

        try {
            const personResponse = await this.props.myPersonRecord();
            const person = {
                flags: 0,
                ...(personResponse || {}),
            };

            if (((person.flags ?? 0) & 8) > 0) {
                const staff = await this.props.myStaffRecord();
                const { success: _staffSuccess, ...staffData } = staff || {};
                person.tax = { ...staffData };
            }

            this.setState({
                person,
                selected: ((person.flags ?? 0) & 8) > 0 || !["email", "withholding", "timecard"].includes(this.state.selected) ? this.state.selected : "profile",
            });

            await this.props.pageInfo({ title: "My Profile", breadcrumb: "Me" });
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to load your profile.", "error");
        } finally {
            this.props.loading(false);
        }
    };

    onSubmit = (e) => {
        e.preventDefault();
        this.onSaveClick(e);
    };

    onPersonChange = (value, errors) => {
        this.setState((prev) => ({
            person: {
                ...(prev.person || {}),
                ...(value || {}),
            },
            errors: errors || {},
        }));
    };

    onTaxChange = (values) => {
        this.setState((prev) => ({
            person: {
                ...(prev.person || {}),
                tax: {
                    ...(prev.person?.tax || {}),
                    ...(values || {}),
                },
            },
        }));
    };

    onCommunicationPreferenceToggle = (flag) => (e) => {
        const checked = Boolean(e.target.checked);

        this.setState((prev) => {
            const current = Number(prev.person?.communicationPreferences || 0);
            const communicationPreferences = checked
                ? (current | flag)
                : (current & ~flag);

            return {
                person: {
                    ...(prev.person || {}),
                    communicationPreferences,
                },
            };
        });
    };

    renderStaffNotificationSettings = () => {
        const communicationPreferences = this.state.person?.communicationPreferences || 0;

        return (
            <>
                <hr className="my-4" />
                <Row className="g-3">
                    <Col lg="12">
                        <h2 className="small-title">Notifications</h2>
                    </Col>

                    <Col md="6">
                        <Form.Check
                            type="switch"
                            id="profile-direct-message-lead-task-event"
                            label="Direct message on lead task event"
                            checked={hasCommunicationPreference(
                                communicationPreferences,
                                COMMUNICATION_PREFERENCES.DIRECT_MESSAGE_LEAD_TASK_EVENT
                            )}
                            onChange={this.onCommunicationPreferenceToggle(
                                COMMUNICATION_PREFERENCES.DIRECT_MESSAGE_LEAD_TASK_EVENT
                            )}
                        />
                    </Col>

                    <Col md="6">
                        <Form.Check
                            type="switch"
                            id="profile-direct-message-board-task-event"
                            label="Direct message on board task event"
                            checked={hasCommunicationPreference(
                                communicationPreferences,
                                COMMUNICATION_PREFERENCES.DIRECT_MESSAGE_BOARD_TASK_EVENT
                            )}
                            onChange={this.onCommunicationPreferenceToggle(
                                COMMUNICATION_PREFERENCES.DIRECT_MESSAGE_BOARD_TASK_EVENT
                            )}
                        />
                    </Col>
                </Row>
            </>
        );
    };

    onSaveClick = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (this.state.saving) return;

        this.setState({ saving: true });
        this.props.loading(true);

        try {
            const personPayload = { ...(this.state.person || {}) };
            const personResponse = await this.props.saveMyPersonRecord(personPayload);
            const { success, ...savedPerson } = personResponse || {};

            if (!success) {
                this.props.sendAlert("Unable to save your profile.", "error");
                return;
            }

            let nextPerson = { ...savedPerson };
            const savedFlags = savedPerson?.flags ?? this.state.person?.flags ?? 0;
            const isStaff = (savedFlags & 8) > 0;

            if (isStaff) {
                const taxResponse = await this.props.myTax({ ...(this.state.person?.tax || {}) });
                const { success: _taxSuccess, ...taxData } = taxResponse || {};
                nextPerson.tax = { ...taxData };
                await this.emailRef.current?.saveSettings?.();
            }

            this.setState({ person: nextPerson });
            this.props.sendAlert("Profile saved.", "success");
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to save your profile.", "error");
        } finally {
            this.setState({ saving: false });
            this.props.loading(false);
        }
    };

    onSharesClick = (e) => {
        if (e?.preventDefault) e.preventDefault();

        this.setState((prev) => ({
            modal: {
                ...(prev.modal || {}),
                shares: {
                    ...(prev.modal?.shares || {}),
                    show: true
                }
            }
        }));
    };

    onSharesClose = () => {
        this.setState((prev) => ({
            modal: {
                ...(prev.modal || {}),
                shares: {
                    ...(prev.modal?.shares || {}),
                    show: false
                }
            }
        }));
    };
    

    onTabChange = (selected) => {
        this.setState({ selected });
    }

    hasReports = () => false;
    reports = [];
    
    
    render() {
        const flags = this.state.person?.flags ?? 0;
        const isStaff = (flags & 8) > 0;

        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Share calendar access"
                        display={isStaff && this.state.selected === "calendar"}
                        enabled={!this.state.saving}
                        onEnabledClick={this.onSharesClick}
                        icon="calendar"
                    >
                        Share Calendars
                    </ConditionalButton>
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Save my profile"
                        display={true}
                        enabled={!this.state.saving}
                        onEnabledClick={this.onSaveClick}
                        icon="save"
                    >
                        {this.state.saving ? "Saving..." : "Save"}
                    </ConditionalButton>
                    <ConditionalDropdown
                        className="ms-sm-1"
                        enabledVariant="secondary"
                        enabled
                        display={typeof this.hasReports === "function" ? this.hasReports() : !!this.hasReports}
                        items={this.reports}
                    >
                        Report
                    </ConditionalDropdown>
                </Col>

                <Tab.Container activeKey={this.state.selected}>
                    <Card.Header className="app-tab-header border-0 pb-0">
                        <Nav
                            className="app-tab-nav nav-tabs-line card-header-tabs"
                            variant="tabs"
                            activeKey={this.state.selected}
                        >
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    className={`${this.state.selected === "profile" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        this.onTabChange("profile");
                                    }}
                                >
                                    Profile
                                </Nav.Link>
                            </Nav.Item>

                            {isStaff ? (
                                <Nav.Item className="text-center">
                                    <Nav.Link
                                        className={`${this.state.selected === "email" ? "active" : ""} app-tab-link`}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            this.onTabChange("email");
                                        }}
                                    >
                                        Email
                                    </Nav.Link>
                                </Nav.Item>
                            ) : null}

                            <Nav.Item className="text-center">
                                <Nav.Link
                                    className={`${this.state.selected === "calendar" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        this.onTabChange("calendar");
                                    }}
                                >
                                    Calendar
                                </Nav.Link>
                            </Nav.Item>

                            {isStaff ? (
                                <Nav.Item className="text-center">
                                    <Nav.Link
                                        className={`${this.state.selected === "withholding" ? "active" : ""} app-tab-link`}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            this.onTabChange("withholding");
                                        }}
                                    >
                                        Withholding
                                    </Nav.Link>
                                </Nav.Item>
                            ) : null}

                            {isStaff ? (
                                <Nav.Item className="text-center">
                                    <Nav.Link
                                        className={`${this.state.selected === "timecard" ? "active" : ""} app-tab-link`}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            this.onTabChange("timecard");
                                        }}
                                    >
                                        Time Card
                                    </Nav.Link>
                                </Nav.Item>
                            ) : null}

                        </Nav>
                    </Card.Header>

                    <Col md="12">
                        <Form name="frm" method="post" onSubmit={this.onSubmit}>
                            <Row>
                                <Col>
                                    <Tab.Content className="app-tab-content-min h-100">
                                        <Tab.Pane active={this.state.selected === "profile"} className="h-100 scroll-out">
                                            <Card body className="mb-5">
                                                <Personal {...this.state.person} onChange={this.onPersonChange} />
                                                {isStaff ? this.renderStaffNotificationSettings() : null}
                                            </Card>
                                        </Tab.Pane>

                                        <Tab.Pane active={this.state.selected === "calendar"} className="h-100 scroll-out">
                                            <Card.Body className="p-0">
                                                <Calendar owner={this.state.person?.uuid} />
                                            </Card.Body>
                                        </Tab.Pane>

                                        {isStaff ? (
                                            <Tab.Pane active={this.state.selected === "email"} className="h-100 scroll-out">
                                                <Card.Body className="p-0">
                                                    <Email
                                                        ref={this.emailRef}
                                                        owner={this.state.person?.uuid}
                                                        displayName={this.state.person?.displayName}
                                                        email={this.state.person?.email}
                                                    />
                                                </Card.Body>
                                            </Tab.Pane>
                                        ) : null}

                                        {isStaff ? (
                                            <Tab.Pane active={this.state.selected === "withholding"} className="h-100 scroll-out">
                                                <Card.Body className="p-0">
                                                    <Tax onChange={this.onTaxChange} show={isStaff} {...(this.state.person?.tax || {})} />
                                                </Card.Body>
                                            </Tab.Pane>
                                        ) : null}

                                        {isStaff ? (
                                            <Tab.Pane active={this.state.selected === "timecard"} className="h-100 scroll-out">
                                                <Row className="g-3 mt-2">
                                                    <Col lg="12">
                                                        <h2 className="small-title">Time Card</h2>
                                                    </Col>
                                                </Row>
                                                <Timecard owner={this.state.person?.uuid} view="employee" />
                                            </Tab.Pane>
                                        ) : null}
                                        
                                    </Tab.Content>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Tab.Container>
                <Shares
                    show={this.state.modal?.shares?.show}
                    onHide={this.onSharesClose}
                />
            </>
        );
    }
}

Profile.propTypes = {
    auth: PropTypes.object.isRequired,
    // Optional depending on parent wiring:
    person: PropTypes.object,
    myPersonRecord: PropTypes.func.isRequired,
    saveMyPersonRecord: PropTypes.func.isRequired,
    myTax: PropTypes.func.isRequired,
    myStaffRecord: PropTypes.func.isRequired,
    pageInfo: PropTypes.func.isRequired,
    loading: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, {
    myPersonRecord,
    saveMyPersonRecord,
    myTax,
    myStaffRecord,
    pageInfo,
    loading,
    sendAlert,
})(Profile);
